import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default function ComplianceStatement() {
  const classes = useStyles();
  const seo = useSEO("compliance-statement");

  return (
    <>
      <StandardPageHelmet seo={seo} />

      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h3" className={classes.title}>
          18 U.S.C. §2257 Record-Keeping Requirements Compliance Statement
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Last updated: October 12, 2025
        </Typography>
        
        <Typography variant="body1" className={classes.paragraph}>
          SugarCribs.com is a content-sharing platform that enables independent creators to upload,
          distribute, and monetize visual content, including adult material, at their sole discretion.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          Pursuant to 18 U.S.C. §2257 and 28 C.F.R. Part 75, SugarCribs.com is not the
          "producer" (primary or secondary) of user-submitted content as defined by the statute.
          SugarCribs.com qualifies as a third-party service provider under 18 U.S.C. §2257(h)(2)(B)
          and does not create, produce, or commission the visual depictions displayed by users on the platform.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          All individuals who upload content to SugarCribs.com must verify they are 18+ and affirm they
          maintain all required age-verification records and compliance documentation mandated by 18 U.S.C. §2257.
          Each uploader is solely responsible for the content they submit and must retain the appropriate model
          records and custodian information. These records are not housed by SugarCribs.com.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Platform Compliance Measures
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Government-issued photo ID verification prior to creator account activation;</li>
          <li className={classes.listItem}>Strict content moderation and rapid takedown processes for violations;</li>
          <li className={classes.listItem}>Timely responses to valid legal inquiries and removal requests.</li>
        </Box>

        <Typography variant="body1" className={classes.paragraph}>
          Inquiries regarding §2257 records for a <em>specific piece of content</em> should be directed to the
          individual creator/uploader who produced that material or their designated Custodian of Records.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Company-Produced Content (If Any)
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          SugarCribs.com does not presently produce visual depictions of actual sexually explicit conduct.
          If the Company produces any such content in the future, it will comply with 18 U.S.C. §2257 for those
          works and publish a separate <strong>Custodian of Records</strong> notice with the required physical
          address and inspection information applicable to those works.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Minors & Reporting
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Content involving minors is strictly prohibited. Alleged violations are reviewed immediately and may be
          reported to law enforcement and/or the National Center for Missing & Exploited Children (NCMEC).
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Contact
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          For compliance or legal inquiries, contact:
        </Typography>

        <Box className={classes.contactInfo}>
          <Typography
            variant="h6"
            style={{ color: "#FE5A97", marginBottom: "16px" }}
          >
            Contact Information
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>Legal Compliance Team</strong>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Email: <a href="mailto:legal@sugarcribs.com" className={classes.link}>legal@sugarcribs.com</a>
          </Typography>
        </Box>
      </Container>
    </>
  );
}
